import { useState, useEffect, useRef } from 'react';
import Waveform from './Waveform';
import { audioBufferToWav } from '../utils/audioUtils';
import { Conversation } from '@11labs/client';
import { PhoneOff, PhoneOutgoing } from 'lucide-react';
import { useSample } from '../SampleContext';
import { useLayout } from '../LayoutContext';

interface AudioRecorderProps {
    setScreen: (screen: 'intro' | 'recorder' | 'uploader' | 'webcam' | 'form') => void;
    nextScreen?: 'webcam' | 'form';
}


// Utility function to validate CSS hex color
const isValidHexColor = (color: string): boolean => {
    return /^#([0-9A-F]{3}){1,2}$/i.test(color);
};

const AudioRecorder: React.FC<AudioRecorderProps> = ({ setScreen, nextScreen = 'form' }) => {
    const { setRecordedBlob, setSampleDuration } = useSample();
    const { layout } = useLayout();
    const [isConnected, setIsConnected] = useState<boolean>(false);
    const [isSpeaking, setIsSpeaking] = useState<boolean>(false);
    const [trimmedDuration, setTrimmedDuration] = useState<number>(0);
    const isSpeakingRef = useRef(isSpeaking);
    const conversationRef = useRef<any>(null);
    const audioContextRef = useRef<AudioContext | null>(null);
    const analyserRef = useRef<AnalyserNode | null>(null);
    const micStreamRef = useRef<MediaStream | null>(null);
    const mediaRecorderRef = useRef<MediaRecorder | null>(null);
    const volumeCheckIntervalRef = useRef<number | null>(null);
    const userWaveRef = useRef<HTMLDivElement[]>(Array(7).fill(null));
    const agentWaveRef = useRef<HTMLDivElement[]>(Array(7).fill(null));
    const speechSegmentsRef = useRef<{ start: number; end: number }[]>([]);
    const recordingStartTimeRef = useRef<number | null>(null);

    // Fallback to default colors if invalid
    const safeColors = {
        buttons: isValidHexColor(layout.color.buttons) ? layout.color.buttons : '#22FFAF',
        text: isValidHexColor(layout.color.text) ? layout.color.text : '#000000',
        hover: isValidHexColor(layout.color.hover) ? layout.color.hover : '#1DE9A3',
    };

    const connectedSVG = <PhoneOff color={safeColors.text} />;
    const defaultSVG = <PhoneOutgoing color={safeColors.text} />;

    const trimAudioBuffer = (buffer: AudioBuffer, segments: { start: number; end: number }[]): AudioBuffer => {
        if (!audioContextRef.current) {
            return buffer;
        }
        const sampleRate = buffer.sampleRate;
        const bufferDuration = buffer.length / sampleRate;

        const MIN_SEGMENT_DURATION = 0.05; // 50ms
        const validSegments = segments
            .filter((segment) => segment.end > segment.start && segment.end - segment.start >= MIN_SEGMENT_DURATION)
            .map((segment) => ({
                start: Math.max(0, segment.start),
                end: Math.min(bufferDuration, segment.end),
            }));

        if (validSegments.length === 0) {
            return audioContextRef.current.createBuffer(
                buffer.numberOfChannels,
                Math.max(1, Math.floor(sampleRate * 0.001)),
                sampleRate
            );
        }

        let totalLength = 0;
        validSegments.forEach((segment) => {
            const startSample = Math.floor(segment.start * sampleRate);
            const endSample = Math.floor(segment.end * sampleRate);
            const segmentLength = endSample - startSample;
            if (segmentLength > 0) {
                totalLength += segmentLength;
            }
        });

        if (totalLength <= 0) {
            return audioContextRef.current.createBuffer(
                buffer.numberOfChannels,
                Math.max(1, Math.floor(sampleRate * 0.001)),
                sampleRate
            );
        }

        const trimmedBuffer = audioContextRef.current.createBuffer(buffer.numberOfChannels, totalLength, sampleRate);

        let offset = 0;
        for (let channel = 0; channel < buffer.numberOfChannels; channel++) {
            const channelData = buffer.getChannelData(channel);
            const trimmedChannelData = trimmedBuffer.getChannelData(channel);
            validSegments.forEach((segment) => {
                const startSample = Math.floor(segment.start * sampleRate);
                const endSample = Math.floor(segment.end * sampleRate);
                const segmentLength = endSample - startSample;
                if (segmentLength > 0 && startSample >= 0 && endSample <= buffer.length) {
                    trimmedChannelData.set(channelData.subarray(startSample, endSample), offset);
                    offset += segmentLength;
                }
            });
            offset = 0;
        }

        return trimmedBuffer;
    };

    const processAudioBlob = async (blob: Blob) => {
        if (!audioContextRef.current) {
            setRecordedBlob(null);
            setScreen(nextScreen);
            return;
        }

        try {
            const arrayBuffer = await blob.arrayBuffer();
            const audioBuffer = await audioContextRef.current.decodeAudioData(arrayBuffer);
            const trimmedBuffer = trimAudioBuffer(audioBuffer, speechSegmentsRef.current);

            if (trimmedBuffer.duration <= 0.001) {
                setRecordedBlob(null);
                setScreen(nextScreen);
                return;
            }

            const offlineContext = new OfflineAudioContext(
                trimmedBuffer.numberOfChannels,
                Math.ceil((trimmedBuffer.length * 22050) / trimmedBuffer.sampleRate),
                22050
            );
            const source = offlineContext.createBufferSource();
            source.buffer = trimmedBuffer;
            source.connect(offlineContext.destination);
            source.start();
            const resampledBuffer = await offlineContext.startRendering();

            const wavBlob = audioBufferToWav(resampledBuffer, 22050, 32);
            setRecordedBlob(wavBlob);
            setSampleDuration(Number(trimmedBuffer.duration.toFixed(1)));
            setScreen(nextScreen);

            speechSegmentsRef.current = [];
            setTrimmedDuration(0);
        } catch (error) {
            setRecordedBlob(null);
            setScreen(nextScreen);
            speechSegmentsRef.current = [];
            setTrimmedDuration(0);
        }
    };

    const toggleConversation = async () => {
        if (!isConnected) {
            try {
                const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
                micStreamRef.current = stream;
                audioContextRef.current = new AudioContext();
                const source = audioContextRef.current.createMediaStreamSource(stream);
                analyserRef.current = audioContextRef.current.createAnalyser();
                analyserRef.current.fftSize = 2048;
                const bufferLength = analyserRef.current.frequencyBinCount;
                const dataArray = new Uint8Array(bufferLength);
                source.connect(analyserRef.current);

                const tracks = stream.getAudioTracks();
                if (tracks.length === 0 || !tracks[0].enabled) {
                    return;
                }

                mediaRecorderRef.current = new MediaRecorder(stream);
                const audioChunks: Blob[] = [];
                mediaRecorderRef.current.ondataavailable = (event: BlobEvent) => {
                    if (event.data.size > 0) {
                        audioChunks.push(event.data);
                    }
                };
                mediaRecorderRef.current.onstop = async () => {
                    const blob = new Blob(audioChunks, { type: 'audio/webm' });
                    await processAudioBlob(blob);
                };

                const volumeHistory: number[] = [];
                const HISTORY_SIZE = 5;
                const SPEECH_THRESHOLD = 9;
                const MIN_SPEECH_DURATION = 200;
                const SILENCE_DURATION = 400;
                const INTERVAL_MS = 100;
                const SILENCE_COUNT = SILENCE_DURATION / INTERVAL_MS;
                let silenceCounter = 0;
                let speechStartTime: number | null = null;

                mediaRecorderRef.current.onstart = () => {
                    recordingStartTimeRef.current = Date.now();
                    speechSegmentsRef.current = [];
                    setTrimmedDuration(0);
                };

                volumeCheckIntervalRef.current = setInterval(() => {
                    if (!analyserRef.current || !recordingStartTimeRef.current) return;
                    analyserRef.current.getByteFrequencyData(dataArray);
                    const avg = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;
                    volumeHistory.push(avg);
                    if (volumeHistory.length > HISTORY_SIZE) {
                        volumeHistory.shift();
                    }
                    const movingAvg = volumeHistory.reduce((a, b) => a + b, 0) / volumeHistory.length;

                    if (movingAvg > SPEECH_THRESHOLD) {
                        if (!speechStartTime) {
                            speechStartTime = Date.now();
                        }
                        silenceCounter = 0;
                        if (Date.now() - speechStartTime >= MIN_SPEECH_DURATION && !isSpeakingRef.current) {
                            setIsSpeaking(true);
                            speechSegmentsRef.current.push({
                                start: (Date.now() - recordingStartTimeRef.current) / 1000,
                                end: 0,
                            });
                        }
                    } else if (isSpeakingRef.current) {
                        silenceCounter++;
                        if (silenceCounter >= SILENCE_COUNT) {
                            setIsSpeaking(false);
                            if (speechSegmentsRef.current.length > 0) {
                                speechSegmentsRef.current[speechSegmentsRef.current.length - 1].end =
                                    (Date.now() - recordingStartTimeRef.current) / 1000;
                            }
                            speechStartTime = null;
                            silenceCounter = 0;
                        }
                    }

                    let totalDuration = 0;
                    speechSegmentsRef.current.forEach((segment) => {
                        if (segment.end > segment.start) {
                            totalDuration += segment.end - segment.start;
                        }
                    });
                    if (isSpeakingRef.current && speechStartTime && speechSegmentsRef.current.length > 0) {
                        const activeDuration = (Date.now() - speechStartTime) / 1000;
                        totalDuration += activeDuration;
                    }
                    setTrimmedDuration(Number(totalDuration.toFixed(1)));
                    setSampleDuration(Number(totalDuration.toFixed(1)));
                }, INTERVAL_MS) as unknown as number;

                conversationRef.current = await Conversation.startSession({
                    agentId: 'eJO0Wgy4ksko99fLKWvY',
                    onConnect: () => {
                        setIsConnected(true);
                        mediaRecorderRef.current?.start();
                    },
                    onDisconnect: () => {
                        setIsConnected(false);
                        mediaRecorderRef.current?.stop();
                        if (isSpeakingRef.current && speechSegmentsRef.current.length > 0) {
                            speechSegmentsRef.current[speechSegmentsRef.current.length - 1].end =
                                (Date.now() - recordingStartTimeRef.current!) / 1000;
                        }
                        cleanupAudio();
                    },
                    onModeChange: (mode: any) => {
                        if (mode.mode === 'speaking') {
                            agentWaveRef.current.forEach((bar, index) => {
                                if (bar) bar.classList.add('waving');
                            });
                        } else {
                            agentWaveRef.current.forEach((bar, index) => {
                                if (bar) bar.classList.remove('waving');
                            });
                        }
                    },
                });
            } catch (error) {
                // Handle errors
            }
        } else {
            if (conversationRef.current) {
                await conversationRef.current.endSession();
                conversationRef.current = null;
            }
        }
    };

    const cleanupAudio = () => {
        if (volumeCheckIntervalRef.current) {
            clearInterval(volumeCheckIntervalRef.current);
        }
        if (audioContextRef.current) {
            audioContextRef.current.close();
        }
        if (micStreamRef.current) {
            micStreamRef.current.getTracks().forEach((t) => t.stop());
        }
        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
            mediaRecorderRef.current.stop();
        }
        setIsSpeaking(false);
    };

    useEffect(() => {
        isSpeakingRef.current = isSpeaking;
        userWaveRef.current.forEach((bar, index) => {
            if (bar) {
                bar.classList.toggle('waving', isSpeaking);
                if (!isSpeaking) {
                    bar.style.height = '10%';
                    bar.classList.remove('waving');
                }
            }
        });
    }, [isSpeaking]);

    useEffect(() => {
        userWaveRef.current = Array(7).fill(null);
        agentWaveRef.current = Array(7).fill(null);
        return () => cleanupAudio();
    }, []);

    const formatDuration = (seconds: number): string => {
        const minutes = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    return (
        <div
            style={{ fontFamily: "'Nunito Sans', sans-serif" }}
            className="relative w-full max-w-[440px] p-8 rounded-xl bg-black shadow-[0_4px_20px_rgba(0,0,0,0.5)] flex flex-col items-center"
        >
            <button
                className="absolute left-[27px] bottom-[24px] border-none rounded-lg px-[12px] py-[6px] font-bold flex items-center gap-3 text-sm cursor-pointer"
                style={{
                    backgroundColor: '#222',
                    color: '#fff',
                    transition: 'background 0.15s',
                }}
                onMouseOver={(e) => (e.currentTarget.style.backgroundColor = '#3a3a3a')}
                onMouseOut={(e) => (e.currentTarget.style.backgroundColor = '#222')}
                onClick={() => {
                    setScreen('intro');
                    setRecordedBlob(null);
                }}
            >
                Back
            </button>
            <h2 className="text-[22px] font-extrabold mb-2" style={{ color: safeColors.buttons }}>Voice Recorder</h2>
            <div className="text-gray-400 mb-3 text-center text-sm leading-relaxed">
                Connect to our Agent to get started on sampling your voice.
            </div>
            <div className="text-gray-500 mb-6 text-center text-xs leading-relaxed" style={{
                backgroundColor: '#111',
                borderRadius: 8,
                padding: '10px 16px',
                border: '1px solid #1a1a1a',
            }}>
                <span style={{ color: safeColors.buttons, fontWeight: 700 }}>Tips:</span> Record in a quiet setting with no background noise. Aim for <span style={{ color: safeColors.buttons, fontWeight: 600 }}>1–2 minutes</span> of audio.
            </div>
            <div className="flex flex-row-reverse gap-[100px] mb-10">
                <div className={`wave-container ${isConnected ? 'flex' : 'hidden'} z-10`}>
                    <Waveform ref={agentWaveRef} />
                </div>
                <div className={`user-wave-container ${isConnected ? 'flex' : 'hidden'} z-10`}>
                    <Waveform isUser ref={userWaveRef} />
                </div>
            </div>
            <button
                className="w-[60px] h-[60px] border-none rounded-full flex justify-center items-center cursor-pointer transition-colors shadow-[0_4px_8px_rgba(0,0,0,0.1)]"
                style={{ backgroundColor: safeColors.buttons, color: safeColors.text }}
                onMouseOver={(e) => (e.currentTarget.style.backgroundColor = safeColors.hover)}
                onMouseOut={(e) => (e.currentTarget.style.backgroundColor = safeColors.buttons)}
                onClick={toggleConversation}
            >
                {isConnected ? connectedSVG : defaultSVG}
            </button>
            {isConnected && (
                <div
                    className="mt-4 text-lg font-bold"
                    style={{
                        color: trimmedDuration < 60
                            ? '#ef4444'
                            : trimmedDuration <= 120
                                ? '#22c55e'
                                : '#ef4444',
                    }}
                >
                    Recorded: {formatDuration(trimmedDuration)}
                    {trimmedDuration >= 120 && (
                        <div className="text-xs font-normal mt-1" style={{ color: '#ef4444' }}>
                            You've hit 2 minutes — please stop the recording.
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default AudioRecorder;